import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ClusterList.css';
import { MoreVertical, Eye, EyeOff, Copy, ChevronRight, ChevronDown, Search, X, Download, KeyRound } from 'lucide-react';
import { useToast } from "../components/Toast";
import Loading from "../components/Loading"
import timeAgo from "../utils/timeAgo";
import useCurrentUser from "../hooks/useCurrentUser";
import { roleHas, CLUSTER_MARK_DELETION, EDGE_CREDS_MANAGE } from "../utils/rolePermissions";

const utcDate = (s) => s ? new Date(s.endsWith("Z") || s.includes("+") ? s : s + "Z") : null;

const ClusterList = () => {
  const navigate = useNavigate();
  const [checkingId, setCheckingId] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const { showToast } = useToast();
  const [showPasswords, setShowPasswords] = useState({ lbadmin: false, admin: false });
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterState, setFilterState] = useState('');
  const [reserveModal, setReserveModal] = useState(null);
  const [reserveCount, setReserveCount] = useState(1);
  const backend_url = process.env.REACT_APP_BACKEND_URL;
  const menuRefs = useRef({});
  const currentUser = useCurrentUser();
  const role = currentUser?.role || '';

  useEffect(() => {
    const fetchClusters = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${backend_url}/api/clusters`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) {
          // Not an array on failure (e.g. RBAC 403 -> {detail: "..."}) — don't
          // pass it to setClusters, and show the real reason instead of an
          // empty/misleading "no clusters" table.
          setClusters([]);
          setLoadError(data?.detail || 'Unable to load clusters.');
          return;
        }
        setClusters(Array.isArray(data) ? data : []);
        setLoadError('');
      } catch (err) {
        console.error('Error fetching clusters:', err);
        setLoadError('Unable to load clusters.');
      } finally {
        setLoading(false);
      }
    };
    fetchClusters();
  }, [reloadTrigger]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (event.target.closest(".actions-menu")) return;
      const clickedInsideMenu = Object.values(menuRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      if (!clickedInsideMenu) setMenuOpen(null);
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleRetryStage = async (cluster) => {
    try {
      setCheckingId(cluster.id);
      const token = localStorage.getItem('authToken');

      const resumeRes = await fetch(`${backend_url}/api/deploy/resumedeployment/${cluster.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const resumeData = await resumeRes.json();
      if (!resumeRes.ok) throw new Error(resumeData.message || "Retry failed");

      showToast(`${resumeData.message}`, "Success");

      localStorage.setItem("activeCluster", cluster.name);
      localStorage.setItem("activeStarterTab", localStorage.getItem("tabId"));
      localStorage.setItem("activeStage", String(cluster.stage - 1));
      localStorage.setItem("activeDeploymentType", cluster.deployment_type || "gke");
      localStorage.setItem("activeEnableHttps", cluster.enable_https ? "1" : "0");

      sessionStorage.setItem('showStatusTab', 'true');
      sessionStorage.setItem('resumeStage', String(cluster.stage - 1));

      navigate('/dashboard/deploy');

    } catch (err) {
      showToast(`${err.message}`, "Fail");
    } finally {
      setCheckingId(null);
      setMenuOpen(null);
    }
  };

  const handleViewCredentials = async (cluster) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${backend_url}/api/deploy/clusters/${cluster.id}/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      if (!data.ok) throw new Error(data.message);
      setCredentials(data.credentials);
      setShowModal(true);
      setMenuOpen(null);
    } catch (err) {
      showToast(`${err.message}`, "Fail");
    }
  };

  const handleCopy = (text, label = "Value") => {
    navigator.clipboard.writeText(text);
    showToast(`${label} copied!`, "Success");
  };

  const handleStateChange = async (cluster, newState) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${backend_url}/api/clusters/${cluster.id}/state`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ state: newState })
      });

      if (!res.ok) throw new Error("Failed to update state");

      if (newState === "Mark for deletion" || newState === "Deleted") {
        const activeCluster = localStorage.getItem("activeCluster");
        if (activeCluster === cluster.name) {
          localStorage.removeItem("activeCluster");
          localStorage.removeItem("activeStarterTab");
          localStorage.removeItem("activeStage");
          setMenuOpen(null);
          window.location.reload();
          return;
        }
      }

      setReloadTrigger(prev => prev + 1);
      setMenuOpen(null);

    } catch (err) {
      console.error("Error updating state:", err);
    }
  };

  const handleReserveEdge = async (cluster) => {
    setReserveModal(cluster);
    setReserveCount(1);
    setMenuOpen(null);
  };

  const submitReserve = async () => {
    if (!reserveModal) return;
    const cluster = reserveModal;
    setReserveModal(null);

    navigate('/dashboard/credentials', {
      state: {
        reserveAction: {
          customerName: cluster.name,
          hubCloud: cluster.cloud || 'gcp',
          count: reserveCount,
        },
      },
    });
  };

  const handleDownloadBundle = async (cluster) => {
    setMenuOpen(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(
        `${backend_url}/api/credentials/download/${cluster.name}?hubCloud=${cluster.cloud || 'gcp'}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Download failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cluster.name}-edge-bundle.tar.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast("Edge bundle downloaded", "Success");
    } catch (err) {
      showToast(err.message, "Fail");
    }
  };

  const owners = [...new Set(clusters.map(c => c.owner).filter(Boolean))];
  const states = [...new Set(clusters.map(c => c.state).filter(Boolean))];

  const filteredClusters = clusters.filter(cluster => {
    const matchesName = !searchName || cluster.name.toLowerCase().includes(searchName.toLowerCase());
    const matchesOwner = !filterOwner || cluster.owner === filterOwner;
    const matchesState = !filterState || cluster.state === filterState;
    return matchesName && matchesOwner && matchesState;
  });

  const hasActiveFilters = searchName || filterOwner || filterState;

  return (
    <div className="cluster-container">
      <h2>Cluster Overview</h2>

      {!loading && (
        <div className="filter-bar">
          <div className="filter-search">
            <Search size={16} className="filter-search-icon" />
            <input
              type="text"
              placeholder="Search clusters..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
            {searchName && (
              <X size={14} className="filter-clear-icon" onClick={() => setSearchName('')} />
            )}
          </div>
          <div className="filter-group">
            <select
              className="filter-select"
              value={filterOwner}
              onChange={(e) => setFilterOwner(e.target.value)}
            >
              <option value="">All Owners</option>
              {owners.map(owner => (
                <option key={owner} value={owner}>{owner}</option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
            >
              <option value="">All States</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
          {hasActiveFilters && (
            <button className="filter-reset" onClick={() => { setSearchName(''); setFilterOwner(''); setFilterState(''); }}>
              Clear
            </button>
          )}
          <span className="filter-count">{filteredClusters.length} of {clusters.length}</span>
        </div>
      )}

      {loading ? (
        <Loading type="skeleton" rows={6} height={52} />
      ) : (
        <table className="cluster-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Cluster Name</th>
              <th>URL</th>
              <th>State</th>
              <th>Progress</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {filteredClusters.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-results">
                  {loadError || "No clusters match the current filters."}
                </td>
              </tr>
            ) : null}
            {filteredClusters.map(cluster => (
              <React.Fragment key={cluster.id}>
                <tr
                  className={`main-row ${expandedRow === cluster.id ? 'expanded' : ''}`}
                  onClick={() => setExpandedRow(expandedRow === cluster.id ? null : cluster.id)}
                >
                  <td className="expand-cell">
                    {expandedRow === cluster.id
                      ? <ChevronDown size={18} className="expand-icon" />
                      : <ChevronRight size={18} className="expand-icon" />
                    }
                  </td>
                  <td className="cluster-name-cell">{cluster.name}</td>
                  <td className="domain-cell" onClick={(e) => e.stopPropagation()}>
                    {cluster.domain ? (
                      <a href={`https://${cluster.domain}`} target="_blank" rel="noopener noreferrer">
                        {cluster.domain}
                      </a>
                    ) : cluster.public_ip && cluster.deployment_type === 'kubeadm_hub' ? (
                      <a href={`http://${cluster.public_ip}`} target="_blank" rel="noopener noreferrer">
                        {`http://${cluster.public_ip}`}
                      </a>
                    ) : "\u2014"}
                  </td>

                  <td>
                    <span className={`status-tag ${cluster.state.toLowerCase().replace(/\s+/g, '-')}`}>
                      {cluster.state}
                    </span>
                  </td>

                  <td>
                    {cluster.progress_status === "complete" && cluster.state === "Running" ? (
                      <span className="progress-tag complete">Complete</span>
                    ) : cluster.progress_status === "in-progress" ? (
                      <span className="progress-tag in-progress">Stage {cluster.stage}</span>
                    ) : cluster.progress_status === "incomplete" ? (
                      <span className="progress-tag incomplete">Stage {cluster.stage} - Failed</span>
                    ) : (
                      <span className="progress-tag pending">Stage {cluster.stage || 1}</span>
                    )}
                  </td>

                  <td className="created-cell" title={cluster.created_at ? utcDate(cluster.created_at).toLocaleString() : ""}>
                    {timeAgo(cluster.created_at)}
                  </td>

                  <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                    <div className="actions-menu" ref={(el) => (menuRefs.current[cluster.id] = el)} style={{ position: "relative" }}>
                      <MoreVertical
                        size={18}
                        className="dots-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(menuOpen === cluster.id ? null : cluster.id);
                        }}
                      />
                      {menuOpen === cluster.id && (
                        <div className="menu-dropdown" onClick={(e) => e.stopPropagation()}>
                          {cluster.state === "Pending" && (
                            <div className="menu-item" onClick={() => handleRetryStage(cluster)}>
                              {checkingId === cluster.id ? "Resuming..." : "Retry Stage"}
                            </div>
                          )}
                          {(cluster.state === "Running" || cluster.state === "Stopped") && (
                            <div className="menu-item" onClick={() => handleViewCredentials(cluster)}>
                              View Credentials
                            </div>
                          )}

                          {cluster.deployment_type === 'kubeadm_hub' && cluster.state === "Running" &&
                           roleHas(role, EDGE_CREDS_MANAGE) && (
                            <>
                              <div className="menu-divider" />
                              <div className="menu-item" onClick={() => handleReserveEdge(cluster)}>
                                <KeyRound size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                Reserve Edge Credentials
                              </div>
                              <div className="menu-item" onClick={() => handleDownloadBundle(cluster)}>
                                <Download size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                Download Edge Bundle
                              </div>
                            </>
                          )}

                          {cluster.state !== "Deleted" && (
                            <>
                              <div className="menu-divider" />
                              {cluster.state !== "Running" && cluster.state !== "Pending" && (
                                <div className="menu-item" onClick={() => handleStateChange(cluster, "Running")}>
                                  Set Running
                                </div>
                              )}
                              {cluster.state !== "Stopped" && cluster.state !== "Pending" && (
                                <div className="menu-item" onClick={() => handleStateChange(cluster, "Stopped")}>
                                  Set Stopped
                                </div>
                              )}
                              {/* Only roles holding cluster:mark_deletion (admin, SE/SA) see this —
                                  CSE/others shouldn't even see the option. Backend enforces too. */}
                              {cluster.state !== "Mark for deletion" && roleHas(role, CLUSTER_MARK_DELETION) && (
                                <div className="menu-item danger" onClick={() => handleStateChange(cluster, "Mark for deletion")}>
                                  Mark for Deletion
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>

                {expandedRow === cluster.id && (
                  <tr className="detail-row">
                    <td colSpan={7}>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <span className="detail-label">Created By</span>
                          <span className="detail-value">{cluster.owner}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Created At</span>
                          <span className="detail-value">{cluster.created_at ? utcDate(cluster.created_at).toLocaleString() : "\u2014"}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Last Updated</span>
                          <span className="detail-value">{cluster.last_updated_at ? utcDate(cluster.last_updated_at).toLocaleString() : "\u2014"}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Nodes</span>
                          <span className="detail-value">{cluster.nodes || "\u2014"}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Region</span>
                          <span className="detail-value">{cluster.region || "\u2014"}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">{cluster.deployment_type === 'kubeadm_hub' ? 'Gateway IP' : 'Public IP'}</span>
                          <span className="detail-value copyable">
                            {cluster.public_ip || "\u2014"}
                            {cluster.public_ip && (
                              <Copy size={14} className="copy-icon" onClick={() => handleCopy(cluster.public_ip, cluster.deployment_type === 'kubeadm_hub' ? "Gateway IP" : "Public IP")} />
                            )}
                          </span>
                        </div>
                        {cluster.deployment_type === 'kubeadm_hub' ? (
                          <>
                            <div className="detail-item">
                              <span className="detail-label">Control Plane SSH</span>
                              <span className="detail-value copyable ssh-command">
                                {cluster.project_id && cluster.zone
                                  ? `gcloud compute ssh ${cluster.name}-lightbeam-control-plane --project=${cluster.project_id} --zone=${cluster.zone} --tunnel-through-iap`
                                  : "\u2014"}
                                {cluster.project_id && cluster.zone && (
                                  <Copy size={14} className="copy-icon" onClick={() => handleCopy(
                                    `gcloud compute ssh ${cluster.name}-lightbeam-control-plane --project=${cluster.project_id} --zone=${cluster.zone} --tunnel-through-iap`,
                                    "Control Plane SSH command"
                                  )} />
                                )}
                              </span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Gateway SSH</span>
                              <span className="detail-value copyable ssh-command">
                                {cluster.project_id && cluster.zone
                                  ? `gcloud compute ssh ${cluster.name}-lightbeam-gateway --project=${cluster.project_id} --zone=${cluster.zone} --tunnel-through-iap`
                                  : "\u2014"}
                                {cluster.project_id && cluster.zone && (
                                  <Copy size={14} className="copy-icon" onClick={() => handleCopy(
                                    `gcloud compute ssh ${cluster.name}-lightbeam-gateway --project=${cluster.project_id} --zone=${cluster.zone} --tunnel-through-iap`,
                                    "Gateway SSH command"
                                  )} />
                                )}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="detail-item">
                            <span className="detail-label">Jumpbox IP</span>
                            <span className="detail-value copyable">
                              {cluster.jumpbox_ip || "\u2014"}
                              {cluster.jumpbox_ip && (
                                <Copy size={14} className="copy-icon" onClick={() => handleCopy(`ssh root@${cluster.jumpbox_ip}`, "SSH command")} />
                              )}
                            </span>
                          </div>
                        )}
                      </div>

                      {cluster.progress_status === "incomplete" &&
                       ((cluster.deployment_type === 'gke' && cluster.stage === 3) ||
                        (cluster.deployment_type === 'kubeadm_hub' && cluster.stage === 3 && cluster.enable_https)) && (
                        <div className="dns-action-banner">
                          <div className="dns-action-title">Action Required: Domain Mapping</div>
                          <p>
                            Please raise an <strong>Infra JIRA ticket</strong> requesting domain mapping with the details below.
                            Once completed, use the <strong>action menu</strong> ( <MoreVertical size={14} style={{ verticalAlign: "middle" }} /> ) to <strong>Retry Stage</strong>.
                          </p>
                          <div className="dns-action-details">
                            <span className="dns-action-item">
                              <strong>Domain:</strong> {cluster.domain}
                              <Copy size={14} className="copy-icon" onClick={() => handleCopy(cluster.domain, "Domain")} />
                            </span>
                            {cluster.public_ip && (
                              <span className="dns-action-item">
                                <strong>Public IP:</strong> {cluster.public_ip}
                                <Copy size={14} className="copy-icon" onClick={() => handleCopy(cluster.public_ip, "Public IP")} />
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {reserveModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Reserve Edge Credentials</h3>
            <div className="cred-row">
              <strong>Cluster</strong>
              <div className="password-box">
                <input type="text" value={reserveModal.name} readOnly />
              </div>
            </div>
            <div className="cred-row">
              <strong>Cloud</strong>
              <div className="password-box">
                <input type="text" value={(reserveModal.cloud || 'gcp').toUpperCase()} readOnly />
              </div>
            </div>
            <div className="cred-row">
              <strong>Number of VMs</strong>
              <div className="password-box">
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={reserveCount}
                  onChange={(e) => setReserveCount(parseInt(e.target.value) || 1)}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="close-btn" onClick={() => setReserveModal(null)}>
                Cancel
              </button>
              <button
                className="close-btn"
                style={{ background: '#3b82f6', color: '#fff' }}
                onClick={submitReserve}
              >
                Reserve
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && credentials && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Cluster Credentials</h3>

            <div className="cred-row">
              <strong>Domain</strong>
              <div className="password-box">
                <input type="text" value={credentials.domain} readOnly />
                <button onClick={() => handleCopy(credentials.domain, "Domain")}>
                  <Copy size={16} />
                </button>
              </div>
            </div>

            {["lbadmin", "admin"].map(key => (
              <div key={key} className="cred-row">
                <strong>{credentials[key].username}</strong>
                <div className="password-box">
                  <input
                    type={showPasswords[key] ? "text" : "password"}
                    value={credentials[key].password}
                    readOnly
                  />
                  <button onClick={() =>
                    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }))
                  }>
                    {showPasswords[key] ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button onClick={() => handleCopy(credentials[key].password, "Password")}>
                    <Copy size={16} />
                  </button>
                </div>
              </div>
            ))}

            <button className="close-btn" onClick={() => setShowModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClusterList;
